import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../components/nav";
import Footer from "./footer";
import VisitorTips from "./VisitorTips";
import "../css/eventDescription.css";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaMusic,
  FaStar,
  FaTag,
  FaTicketAlt,
  FaUsers,
} from "react-icons/fa";
import { notifyInfo } from "../utils/notify";

const getPriceValue = (price) => {
  if (typeof price === "number") return price;
  if (typeof price !== "string") return 0;
  const match = price.match(/\d+(?:[.,]\d+)?/);
  return match ? Number(match[0].replace(",", ".")) : 0;
};

const formatPrice = (price) => {
  const value = getPriceValue(price);
  if (!value) return "Contact for price";
  return `R ${value.toLocaleString("en-ZA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const isRealValue = (value) =>
  value && value !== "Not specified" && value !== "Location not specified";

const EventDescription = () => {
  const navigate = useNavigate();
  const { state = {} } = useLocation();
  const [selectedImage, setSelectedImage] = useState("");

  const gallery = useMemo(() => {
    const images = Array.isArray(state.gallery) ? state.gallery.filter(Boolean) : [];
    return [...new Set([state.image, ...images].filter(Boolean))];
  }, [state.gallery, state.image]);

  useEffect(() => {
    setSelectedImage(gallery[0] || "");
  }, [gallery]);

  const {
    title = "Event",
    description = "No description available.",
    location: eventLocation = "Location not specified",
    lineUp = [],
    startDate = "Not specified",
    endDate = "Not specified",
    startTime = "Not specified",
    endTime = "Not specified",
    category = "Event",
    price = 0,
    ticketLink = "",
    organizer = "Organizer not specified",
  } = state;

  const dateLabel =
    isRealValue(startDate) && startDate === endDate
      ? `${startDate}${isRealValue(startTime) ? `, ${startTime}` : ""}${
          isRealValue(endTime) ? ` - ${endTime}` : ""
        }`
      : isRealValue(startDate) || isRealValue(endDate)
      ? `${startDate} - ${endDate}`
      : "Date to be confirmed";

  const durationLabel =
    isRealValue(startDate) && startDate === endDate
      ? "One day event"
      : isRealValue(startDate) && isRealValue(endDate)
      ? "Multi-day event"
      : "Duration to be confirmed";

  const handleTicketClick = (event) => {
    if (!ticketLink || ticketLink === "#") {
      event.preventDefault();
      notifyInfo("Tickets are not available yet. Please check back soon.");
    }
  };

  if (!state.title) {
    return (
      <div className="event-description-page">
        <NavBar />
        <main className="event-detail-container">
          <button className="event-back-button" onClick={() => navigate(-1)} type="button">
            <FaArrowLeft />
            Back
          </button>
          <section className="event-empty-state">
            <h1>Event details unavailable</h1>
            <p>Please return to the events list and choose an event.</p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="event-description-page">
      <NavBar />
      <main className="event-detail-container">
        <header className="event-title-row">
          <button className="event-back-button icon-only" onClick={() => navigate(-1)} type="button">
            <FaArrowLeft />
          </button>
          <div>
            <p className="event-eyebrow">
              <FaTag />
              {category}
            </p>
            <h1>{title}</h1>
            <div className="event-meta-row">
              <span>
                <FaMapMarkerAlt />
                {eventLocation}
              </span>
              <span>
                <FaCalendarAlt />
                {dateLabel}
              </span>
            </div>
          </div>
        </header>

        <section className="event-hero-grid">
          <div className="event-main-image">
            {selectedImage ? (
              <img src={selectedImage} alt={title} />
            ) : (
              <div className="event-image-placeholder">
                <FaMusic />
                <span>{category}</span>
              </div>
            )}
            <div className="event-hero-overlay">
              <span>{formatPrice(price)} per ticket</span>
              <strong>{durationLabel}</strong>
            </div>
          </div>

          <div className="event-thumbnail-grid">
            {gallery.slice(0, 4).map((src, index) => (
              <button
                key={src}
                className={`event-thumbnail ${selectedImage === src ? "active" : ""}`}
                type="button"
                onClick={() => setSelectedImage(src)}
              >
                <img src={src} alt={`${title} view ${index + 1}`} />
              </button>
            ))}
            {!gallery.length && (
              <div className="event-thumbnail empty">
                <FaMusic />
              </div>
            )}
          </div>
        </section>

        <section className="event-content-grid">
          <div className="event-main-content">
            <div className="event-section event-facts">
              <h2>Event Information</h2>
              <div className="event-info-grid">
                <div className="event-info-item cyan">
                  <span>
                    <FaMapMarkerAlt />
                  </span>
                  <div>
                    <strong>Location</strong>
                    <p>{eventLocation}</p>
                  </div>
                </div>
                <div className="event-info-item blue">
                  <span>
                    <FaUsers />
                  </span>
                  <div>
                    <strong>Organizer</strong>
                    <p>{organizer}</p>
                  </div>
                </div>
                <div className="event-info-item amber">
                  <span>
                    <FaCalendarAlt />
                  </span>
                  <div>
                    <strong>Date & Time</strong>
                    <p>{dateLabel}</p>
                  </div>
                </div>
                <div className="event-info-item green">
                  <span>
                    <FaTicketAlt />
                  </span>
                  <div>
                    <strong>Ticket Price</strong>
                    <p>{formatPrice(price)} per ticket</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="event-section">
              <h2>About This Event</h2>
              <p>{description}</p>
            </div>

            <div className="event-section">
              <h2>Line Up</h2>
              {lineUp.length > 0 ? (
                <div className="event-lineup">
                  {lineUp.map((artist, index) => (
                    <span key={`${artist}-${index}`}>
                      <FaStar />
                      {artist}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="event-muted">Lineup information will be announced soon.</p>
              )}
            </div>
          </div>

          <aside className="event-ticket-panel">
            <div className="event-ticket-card">
              <span className="event-ticket-icon">
                <FaTicketAlt />
              </span>
              <h2>Get Your Tickets</h2>
              <p>Secure your spot and keep the event details handy.</p>

              <div className="event-ticket-summary">
                <div>
                  <span>Price</span>
                  <strong>{formatPrice(price)}</strong>
                </div>
                <div>
                  <span>When</span>
                  <strong>{dateLabel}</strong>
                </div>
                <div>
                  <span>Duration</span>
                  <strong>
                    <FaClock />
                    {durationLabel}
                  </strong>
                </div>
              </div>

              <a
                href={ticketLink || "#"}
                className={`event-ticket-button ${
                  ticketLink && ticketLink !== "#" ? "" : "disabled"
                }`}
                onClick={handleTicketClick}
                target={ticketLink && ticketLink !== "#" ? "_blank" : undefined}
                rel={ticketLink && ticketLink !== "#" ? "noopener noreferrer" : undefined}
              >
                <FaTicketAlt />
                {ticketLink && ticketLink !== "#" ? "Buy Tickets Now" : "Tickets Coming Soon"}
              </a>
            </div>
          </aside>
        </section>
      </main>
      <section className="detail-extra-section">
        <VisitorTips title={title} category="Event" />
      </section>
      <Footer />
    </div>
  );
};

export default EventDescription;
